import { Component, computed, effect, inject, input, model, signal, ViewEncapsulation } from '@angular/core'
import { DayUI, VetSchedulePlannerService } from '../vet-schedule-planner.service'
import { Day, VSPDay_Type, VSPShift_Type, Shift, VSPWeek_Status, Doctor } from 'src/app/shared/services/ppv.api'
import { ToastrService } from 'ngx-toastr'
import * as EnumHelper from '../enum-helper'
import { forkJoin, Observable, of, switchMap } from 'rxjs'

@Component({
   selector: 'vsp-day-dialog',
   standalone: false,
   templateUrl: './day-dialog.component.html',
   styleUrls: ['./day-dialog.component.scss'],
   encapsulation: ViewEncapsulation.None,
})
export class DayDialogComponent {
   service = inject(VetSchedulePlannerService)

   def_startTime = new Date('2000-01-01T08:00:00')
   def_endTime = new Date('2000-01-01T18:00:00')

   startTime = model<Date | null>(this.def_startTime)
   endTime = model<Date | null>(this.def_endTime)
   shiftType = model<VSPShift_Type | null>(null)
   dayType = model<VSPDay_Type | null>(null)
   shifts = signal<any[]>([])
   editedShift = signal<any>(null)

   dtChoices = EnumHelper.enumToValueLabelArray(EnumHelper.dayTypes).filter((dt) => dt.value > VSPDay_Type.Working && dt.value != VSPDay_Type.Remove)

   stChoices = EnumHelper.enumToValueLabelArray(EnumHelper.shiftTypes)

   daysOfWeek = computed(() => {
      var retval = this.service.weekdays.map((d, i) => ({
         label: d.substring(0, 3),
         value: i,
         disabled: this.service.week().isDayClosed[i],
      }))
      console.log('days of week computed:', retval)
      return retval
   })

   doctors = computed(() => {
      let retval =
         this.service.week()?.doctors.map((d) => {
            let retval = {
               label: d.firstName + ' ' + d.lastName,
               value: d,
            }
            return retval
         }) || []
      console.log('doctors computed:', retval)
      return retval
   })

   copyToDays = model<number[]>([])
   copyToDoctors = model<Doctor[]>([])

   /*isShiftValid = computed((): boolean => {
      let allFieldsSet = !!this.startTime() && !!this.endTime() && this.startTime() < this.endTime() && this.shiftType() > 0
      let noOverlap = this.shifts().every((s: any) => s.endTimeDate <= this.startTime() || s.startTimeDate >= this.endTime())
      return allFieldsSet && noOverlap
   })*/

   isShiftValid = computed((): boolean => {
      const start = this.startTime()
      const end = this.endTime()

      let shifts = this.shifts().filter((s) => s !== this.editedShift())

      if (EnumHelper.isTimelessShiftType(this.shiftType())) {
         let hasWorkingShift = shifts.length > 0 && shifts.some((s: any) => !EnumHelper.isTimelessShiftType(s.type))
         if (hasWorkingShift || !EnumHelper.needsWorkingShift(this.shiftType())) return true
         else return false
      }

      let allFieldsSet = !!start && !!end && start < end && this.shiftType() > 0
      // Check if end time is past midnight
      if (end && end.getHours() === 0 && end.getMinutes() > 0) {
         return false
      }

      let noOverlap = shifts.every((s: any) => EnumHelper.isTimelessShiftType(s.type) || s.endTimeDate <= start || s.startTimeDate >= end)

      return allFieldsSet && noOverlap
   })

   isTimelessShiftType = computed((): boolean => {
      if (this.shiftType() > 0 && EnumHelper.isTimelessShiftType(this.shiftType())) {
         return true
      }
      return false
   })

   onEndTimeChange() {
      const end = this.endTime()
      if (end && end.getHours() === 0 && end.getMinutes() > 0) {
         this.toastr.warning('End time cannot go past 12:00 AM. Shifts must be split by day.', '', {
            timeOut: 6000,
            positionClass: 'toast-center-center',
         })
      }
   }

   onTimeChange(timeSignal) {
      let time: Date = timeSignal()
      if (time && (time.getFullYear() !== 2000 || time.getMonth() !== 0 || time.getDate() !== 1)) {
         time.setFullYear(2000, 0, 1)
         timeSignal.set(time)
      }
   }

   shiftLength = computed((): number => {
      let s = this.shifts()
      return s?.length ?? 0
   })

   isValid = computed((): boolean => {
      return (this.shiftLength() > 0 && this.shifts().some((s) => !EnumHelper.needsWorkingShift(s.type))) || this.dayType() > 1
   })

   constructor(private toastr: ToastrService) {
      effect(() => {
         console.log('Day dialog visible changed:', this.service.dayDialogVisible())
         this.copyToDays.set([])
         this.copyToDoctors.set([])
      })
   }

   onDialogShow() {
      this.startTime.set(this.def_startTime)
      this.endTime.set(this.def_endTime)
      this.shiftType.set(null)
      this.dayType.set(this.service.isWorkDay() ? VSPDay_Type.Working : this.service.selectedDay().type || null)
      this.shifts.set(
         this.service.selectedDay().shifts?.map((s) => {
            return {
               startTimeDate: s.startTime == null ? null : this.service.timeSpanStringToDate(s.startTime),
               endTimeDate: s.endTime == null ? null : this.service.timeSpanStringToDate(s.endTime),
               type: s.type,
               typeName: EnumHelper.shiftTypes[s.type],
            }
         }) || []
      )
   }

   onDialogHide() {
      this.startTime.set(this.def_startTime)
      this.endTime.set(this.def_endTime)
      this.shiftType.set(null)
      this.dayType.set(null)
      this.shifts.set([])
   }

   removeShift(shift: any) {
      this.shifts.set(this.shifts().filter((s) => s !== shift))
   }

   editShift(shift: any) {
      this.shiftType.set(shift.type)
      this.startTime.set(shift.startTimeDate)
      this.endTime.set(shift.endTimeDate)
      this.editedShift.set(shift)
   }

   cancelEditShift() {
      this.editedShift.set(null)
      this.startTime.set(this.def_startTime)
      this.endTime.set(this.def_endTime)
      this.shiftType.set(null)
   }

   saveEditedShift() {
      this.removeShift(this.editedShift())
      this.addShift()
      this.editedShift.set(null)
   }

   cancel() {
      this.service.dayDialogVisible.set(false)
   }

   save() {
      if (this.service.isLoading()) {
         return
      }
      this.service.isLoading.set(true)
      let toSave: Partial<Day> = {
         type: this.dayType(),
         shifts:
            this.dayType() === VSPDay_Type.Working
               ? this.shifts().map((s) => {
                    return {
                       type: s.type,
                       startTime: s.startTimeDate?.toTimeString().substring(0, 8),
                       endTime: s.endTimeDate?.toTimeString().substring(0, 8),
                    } as Shift
                 })
               : null,
      }
      let updates: Array<Observable<number>> = []
      let doctors: Doctor[] = []
      updates.push(this.service.week().id ? of(this.service.week().id) : this.service.saveWeekChanges())
      for (let doc of this.service.week()!.doctors) {
         if (this.service.selectedDoctor() == doc || this.copyToDoctors().includes(doc)) {
            updates.push(doc.id ? of(doc.id) : this.service.saveDoctorChanges(doc, doc))
            doctors.push(doc)
         }
      }
      forkJoin(updates)
         .pipe(
            switchMap(() => {
               let dayUpdates: Array<Observable<number>> = []
               for (let doc of doctors) {
                  for (let day of doc.days) {
                     if (
                        (day == this.service.selectedDay() && this.service.selectedDoctor() == doc) ||
                        (this.copyToDays().includes(day.weekday) && this.copyToDoctors().includes(doc))
                     ) {
                        dayUpdates.push(this.service.saveDayChanges(toSave, doc, day))
                     }
                  }
               }
               return forkJoin(dayUpdates)
            })
         )
         .subscribe({
            //this.service.saveDayChanges(toSave).subscribe({
            next: () => {
               this.service.dayDialogVisible.set(false)
               this.toastr.success('Changes saved successfully.')
            },
            complete: () => {
               this.service.isLoading.set(false)
            },
            error: (e) => {
               this.service.isLoading.set(false)
               this.service.showError('Failed to save changes.')
            },
         })
   }

   addShift() {
      this.shifts.set(this.shifts() || [])
      let s = this.shifts()
      s.push({
         startTimeDate: this.isTimelessShiftType() ? undefined : this.startTime(),
         endTimeDate: this.isTimelessShiftType() ? undefined : this.endTime(),
         type: this.shiftType(),
         typeName: EnumHelper.shiftTypes[this.shiftType()],
      } as any)
      this.shifts.set(
         [...s].sort(
            (a, b) =>
               (EnumHelper.isTimelessShiftType(a.type) ? 1 : 0) - (EnumHelper.isTimelessShiftType(b.type) ? 1 : 0) ||
               a.startTimeDate - b.startTimeDate ||
               a.type - b.type
         )
      )
      this.startTime.set(this.def_startTime)
      this.endTime.set(this.def_endTime)
      this.shiftType.set(null)
   }

   onRadioGroupChange(a: any) {
      this.save()
   }
}
